import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FileText, AlertTriangle, TrafficCone,
  BarChart2, Settings, Users, BookOpen,
  ChevronLeft, ChevronRight, ChevronDown, Bell, TrendingUp,
  ClipboardList, CalendarDays, MessageSquare,
  Bus, UserCheck, Bug, LifeBuoy, History, ShieldCheck, Scale, BookText, Hash, Route,
  Clock4, KeyRound, Wrench, ClipboardCheck, ListChecks, FileLock2, Gauge,
  Activity, StickyNote, MapPin, Map as MapIcon, Umbrella, Inbox, Gavel, Wine, FileCheck2,
  FolderOpen, Network, BarChart3, CalendarClock, BusFront,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useUIStore } from '@store/ui.store';
import { useAuthStore } from '@store/auth.store';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useSidebarBadges } from '@/shared/hooks/useSidebarBadges';
import { APP_VERSION, APP_VERSION_DATE } from '@/lib/version';

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  /**
   * RBAC permission key required to see this item (matrix on /settings/roles).
   * Omitted ⇒ visible to every authenticated user. ADMIN always sees everything.
   * Must stay in sync with the route guards in App.tsx.
   */
  permission?: string;
  iconColor: string; // static text-color class for the inactive icon
  color: string;     // per-item accent hex — drives the active highlight + glow
}

/**
 * B-679: rozwijana grupa powiązanych pozycji (wzór z NEXOPS). Sama nie ma adresu ani uprawnienia —
 * widać ją, gdy widać choć jedną pozycję; przy jednej widocznej pozycji menu pokazuje tę pozycję zamiast grupy.
 */
interface NavGroup {
  label: string;
  icon: React.ElementType;
  iconColor: string;
  color: string;
  children: NavItem[];
}

type NavEntry = NavItem | NavGroup;

interface NavSection {
  /** Bez tytułu = sekcja bez nagłówka (pierwsza, codzienne wejścia). */
  title?: string;
  items: NavEntry[];
}

const isGroup = (e: NavEntry): e is NavGroup => 'children' in e;

// ─── Navigation config ────────────────────────────────────────────────────────
// NOTE: iconColor strings must be *complete literal* Tailwind class names so the
// JIT scanner keeps them in the bundle. `color` is an inline hex used for the
// active tinted background, icon-pill glow, accent bar and inline badge pill.
// B-679 (29.10): 42 pozycje z 9 sekcji ułożone w 3 sekcje i rozwijane grupy; adresy i klucze bez zmian.

const NAV_SECTIONS: NavSection[] = [
  {
    // Do 29.10 sekcja „Główne”. Codzienne wejścia zostają pojedynczymi pozycjami.
    items: [
      { label: 'Pulpit',          href: '/',         icon: LayoutDashboard,                            iconColor: 'text-teal-400', color: '#14b8a6' },
      // B-537: mapa floty jest widokiem „na co dzień”, więc stoi na górze menu (do 06.10 była w Operacjach, pod Dyspozycją).
      // B-519: za uprawnieniem „Mapa pojazdów” (fleet.map.view; domyślnie kierownik zmiany i instruktor, ADMIN zawsze) —
      // tym samym kluczem co trasa w App.tsx i GET /fleet-position/live. Do 05.10 (B-498) mapę widział każdy zalogowany.
      { label: 'Mapa pojazdów',   href: '/mapa-pojazdow', icon: MapIcon,    permission: 'fleet.map.view', iconColor: 'text-emerald-400', color: '#10b981' },
      { label: 'Tablica',         href: '/board',    icon: StickyNote,      permission: 'board.manage', iconColor: 'text-amber-400', color: '#f59e0b' },
      { label: 'Dziennik służby', href: '/duty-log', icon: BookText,        permission: 'dutylog.view', iconColor: 'text-blue-400', color: '#3b82f6' },
      {
        label: 'Formularze', icon: FileText, iconColor: 'text-blue-400', color: '#3b82f6',
        children: [
          { label: 'Moje formularze',    href: '/forms/my',        icon: FileText,                           iconColor: 'text-blue-400', color: '#3b82f6' },
          { label: 'Dokumenty',          href: '/admin/documents', icon: FileLock2, permission: 'forms.manage', iconColor: 'text-teal-400', color: '#14b8a6' },
          { label: 'Kreator formularzy', href: '/admin/forms',     icon: BookOpen,  permission: 'forms.manage', iconColor: 'text-cyan-400', color: '#06b6d4' },
        ],
      },
      {
        label: 'Komunikacja', icon: MessageSquare, iconColor: 'text-emerald-400', color: '#10b981',
        children: [
          { label: 'Wiadomości',    href: '/messages',      icon: MessageSquare, iconColor: 'text-emerald-400', color: '#10b981' },
          { label: 'Powiadomienia', href: '/notifications', icon: Bell,          iconColor: 'text-amber-400',   color: '#f59e0b' },
        ],
      },
    ],
  },
  {
    title: 'Operacje',
    items: [
      {
        label: 'Planowanie', icon: CalendarDays, iconColor: 'text-indigo-400', color: '#6366f1',
        children: [
          { label: 'Planer',              href: '/planner',        icon: CalendarDays,  permission: 'planner.manage', iconColor: 'text-indigo-400', color: '#6366f1' },
          { label: 'Dyspozycja',          href: '/admin/tasks',    icon: ClipboardList, permission: 'planner.manage', iconColor: 'text-violet-400', color: '#8b5cf6' },
          // B-683 E3: plan z definicji GAiT na dziś i 5 dni do przodu. Ten sam klucz co trasa w App.tsx i API rozkładu (GET /timetable/days, /timetable/duties).
          { label: 'Rozkład',             href: '/rozklad',        icon: CalendarClock, permission: 'timetable.view', iconColor: 'text-indigo-400', color: '#6366f1' },
          // Szablony czynności planowanych w Planerze — ten sam klucz (do 29.10 w sekcji „Dane”).
          { label: 'Definicje czynności', href: '/task-templates', icon: ListChecks,    permission: 'planner.manage', iconColor: 'text-teal-400',   color: '#14b8a6' },
        ],
      },
      {
        label: 'Realizacja', icon: Activity, iconColor: 'text-lime-400', color: '#84cc16',
        children: [
          { label: 'Wykonanie',     href: '/execution',  icon: Activity,   permission: 'worktime.view',  iconColor: 'text-lime-400',    color: '#84cc16' },
          // B-676: przegląd wystawionych kart drogowych. Ten sam klucz co trasa w App.tsx i GET /road-cards/overview.
          { label: 'Plan wozów',    href: '/realizacja-wozow', icon: BusFront, permission: 'reports.view', iconColor: 'text-lime-400', color: '#84cc16' },
          { label: 'Karty drogowe', href: '/road-cards', icon: FileCheck2, permission: 'roadcards.view', iconColor: 'text-lime-400',    color: '#65a30d' },
          { label: 'RCP',           href: '/worktime',   icon: Clock4,     permission: 'worktime.view',  iconColor: 'text-emerald-400', color: '#10b981' },
        ],
      },
      {
        label: 'Zdarzenia', icon: AlertTriangle, iconColor: 'text-orange-400', color: '#f97316',
        children: [
          { label: 'Zdarzenia',   href: '/incidents',   icon: AlertTriangle, permission: 'incidents.view',   iconColor: 'text-orange-400', color: '#f97316' },
          { label: 'Awarie',      href: '/failures',    icon: Wrench,        permission: 'failures.view',    iconColor: 'text-red-400',    color: '#ef4444' },
          { label: 'Utrudnienia', href: '/disruptions', icon: TrafficCone,   permission: 'disruptions.view', iconColor: 'text-amber-400',  color: '#d97706' },
        ],
      },
      {
        label: 'Kontrole', icon: ClipboardCheck, iconColor: 'text-rose-400', color: '#f43f5e',
        children: [
          // B-655: rejestr wszystkich pomiarów trzeźwości. Tym samym kluczem co trasa w App.tsx i GET /sobriety-checks
          // (domyślnie role z dostępem do rejestru; ADMIN zawsze). Do 26.10 stał w Głównych, pod Formularzami.
          { label: 'Kontrole trzeźwości', href: '/forms/kontrole-trzezwosci', icon: Wine, permission: 'sobriety.registry.view', iconColor: 'text-rose-400', color: '#f43f5e' },
          // B-675: rejestr kontroli prędkości — ten sam klucz co trasa w App.tsx i GET /speed-checks.
          { label: 'Kontrole prędkości', href: '/forms/kontrole-predkosci', icon: Gauge, permission: 'speed.registry.view', iconColor: 'text-amber-400', color: '#f59e0b' },
          { label: 'Przeglądy pojazdów', href: '/inspections', icon: ClipboardCheck, permission: 'inspections.view', iconColor: 'text-sky-400', color: '#0ea5e9' },
        ],
      },
      {
        label: 'Analizy', icon: BarChart3, iconColor: 'text-sky-400', color: '#0ea5e9',
        children: [
          { label: 'Statystyki', href: '/analytics', icon: TrendingUp, permission: 'analytics.view', iconColor: 'text-emerald-400', color: '#10b981' },
          { label: 'Raporty',    href: '/reports',   icon: BarChart2,  permission: 'reports.view',   iconColor: 'text-sky-400',     color: '#0ea5e9' },
        ],
      },
    ],
  },
  {
    title: 'Zasoby',
    items: [
      {
        label: 'Kartoteki', icon: FolderOpen, iconColor: 'text-blue-400', color: '#3b82f6',
        children: [
          { label: 'Kierowcy',     href: '/admin/drivers',  icon: UserCheck, permission: 'drivers.manage',  iconColor: 'text-blue-400',   color: '#3b82f6' },
          { label: 'Weryfikacja CEPiK', href: '/admin/cepik', icon: ShieldCheck, permission: 'drivers.cepik.view', iconColor: 'text-teal-400', color: '#0d9488' }, // B-705
          { label: 'Instruktorzy', href: '/instructors',    icon: Users,     permission: 'drivers.manage',  iconColor: 'text-indigo-400', color: '#6366f1' },
          { label: 'Pojazdy',      href: '/admin/vehicles', icon: Bus,       permission: 'vehicles.manage', iconColor: 'text-orange-400', color: '#f97316' },
          { label: 'Urządzenia',   href: '/admin/devices',  icon: Gauge,     permission: 'vehicles.manage', iconColor: 'text-rose-400',   color: '#f43f5e' },
        ],
      },
      {
        label: 'Sieć', icon: Network, iconColor: 'text-violet-400', color: '#8b5cf6',
        children: [
          { label: 'Linie / Trasy',   href: '/admin/lines',         icon: Route,  permission: 'lines.manage',        iconColor: 'text-violet-400', color: '#8b5cf6' },
          { label: 'Punkty socjalne', href: '/admin/social-points', icon: MapPin, permission: 'socialpoints.manage', iconColor: 'text-amber-400',  color: '#d97706' }, // „Stan piasku” to zakładka tej strony
        ],
      },
      {
        // Moduł Ubezpieczenia i szkody (Admin 1.687.0). Podstrony mają też zakładki w nagłówku modułu (zostają, B-679 D5).
        label: 'Ubezpieczenia', icon: Umbrella, iconColor: 'text-sky-400', color: '#0ea5e9',
        children: [
          { label: 'Polisy',          href: '/insurance',         icon: Umbrella,      permission: 'insurance.view', iconColor: 'text-sky-400',    color: '#0ea5e9' },
          { label: 'Do kwalifikacji', href: '/insurance/queue',   icon: Inbox,         permission: 'claims.view',    iconColor: 'text-sky-400',    color: '#38bdf8' },
          { label: 'Szkody',          href: '/insurance/claims',  icon: ClipboardList, permission: 'claims.view',    iconColor: 'text-sky-400',    color: '#0284c7' },
          { label: 'Przetargi',       href: '/insurance/tenders', icon: Gavel,         permission: 'tenders.manage', iconColor: 'text-indigo-400', color: '#6366f1' },
        ],
      },
    ],
  },
  {
    title: 'Administracja',
    items: [
      {
        label: 'System', icon: Settings, iconColor: 'text-slate-400', color: '#64748b',
        children: [
          { label: 'Użytkownicy',        href: '/admin/users',        icon: Users,    permission: 'users.manage',        iconColor: 'text-pink-400',  color: '#ec4899' },
          { label: 'Role i uprawnienia', href: '/settings/roles',     icon: KeyRound, permission: 'roles.manage',        iconColor: 'text-blue-400',  color: '#2b8fe0' },
          { label: 'Ustawienia',         href: '/admin/system',       icon: Settings, permission: 'settings.manage',     iconColor: 'text-slate-400', color: '#64748b' },
          { label: 'Słowniki',           href: '/admin/dictionaries', icon: BookOpen, permission: 'dictionaries.manage', iconColor: 'text-cyan-400',  color: '#06b6d4' },
          { label: 'Numeracja',          href: '/admin/numbering',    icon: Hash,     permission: 'numbering.manage',    iconColor: 'text-cyan-400',  color: '#22d3ee' },
          { label: 'Audyt log',          href: '/admin/audit',        icon: History,  permission: 'audit.view',          iconColor: 'text-amber-400', color: '#f59e0b' },
        ],
      },
      {
        label: 'Prawo', icon: Scale, iconColor: 'text-slate-300', color: '#94a3b8',
        children: [
          { label: 'Centrum prawne',      href: '/legal',       icon: Scale,                                  iconColor: 'text-slate-300',   color: '#94a3b8' },
          { label: 'Dane osobowe (RODO)', href: '/admin/gdpr',  icon: FileLock2,   permission: 'gdpr.export', iconColor: 'text-violet-400',  color: '#8b5cf6' },
          { label: 'Zgody prawne',        href: '/admin/legal', icon: ShieldCheck, permission: 'legal.view',  iconColor: 'text-emerald-400', color: '#10b981' },
        ],
      },
      { label: 'Zgłoszenia', href: '/feedback/mine', icon: Bug,      iconColor: 'text-rose-400', color: '#f43f5e' },
      { label: 'Pomoc',      href: '/pomoc',         icon: LifeBuoy, iconColor: 'text-sky-400',  color: '#0ea5e9' },
    ],
  },
];

const ALL_NAV_HREFS = NAV_SECTIONS.flatMap((s) => s.items.flatMap((e) => (isGroup(e) ? e.children : [e]).map((i) => i.href)));

// „/insurance” i „/insurance/claims” leżą pod jednym prefiksem — aktywna jest
// tylko pozycja z NAJDŁUŻSZYM pasującym adresem, żeby nie świeciły się dwie naraz.
function isHrefActive(href: string, pathname: string) {
  return href === '/'
    ? pathname === '/'
    : pathname.startsWith(href) &&
      !ALL_NAV_HREFS.some((h) => h.length > href.length && h.startsWith(href) && pathname.startsWith(h));
}

/** Kolor widoku = kolor aktywnej pozycji menu; karty biorą z niego obwódkę, rozbłysk i cień (AppLayout). */
export function viewAccentFor(pathname: string): string {
  for (const s of NAV_SECTIONS) {
    for (const e of s.items) {
      for (const i of isGroup(e) ? e.children : [e]) if (isHrefActive(i.href, pathname)) return i.color;
    }
  }
  return '#14b8a6';
}

// ─── Badges ───────────────────────────────────────────────────────────────────

type BadgeTone = 'primary' | 'amber' | 'red' | 'green' | 'rose';

const TONE_HEX: Record<BadgeTone, string> = {
  amber: '#f59e0b', red: '#dc2626', green: '#10b981', rose: '#f43f5e', primary: '#0d9488',
};
/** Zwinięta grupa pokazuje sumę liczników w najpilniejszym kolorze spośród swoich pozycji. */
const TONE_RANK: Record<BadgeTone, number> = { red: 4, amber: 3, rose: 2, green: 1, primary: 0 };

function badgeFor(href: string, badges: ReturnType<typeof useSidebarBadges>): { count: number; tone: BadgeTone } {
  const count =
    href === '/notifications' ? badges.notifs      :
    href === '/messages'      ? badges.messages    :
    href === '/forms/my'      ? badges.forms       :
    href === '/incidents'     ? badges.incidents   :
    href === '/failures'      ? badges.failures    :
    href === '/inspections'   ? badges.inspections :
    href === '/duty-log'      ? badges.dutyLog     :
    href === '/pomoc'         ? badges.help        :
    href === '/feedback'      ? badges.feedback    :
    href === '/feedback/mine' ? badges.feedback    :
    href === '/board'         ? badges.board       :
    href === '/admin/tasks'   ? badges.dutyPlan    :
    0;
  const tone: BadgeTone =
    href === '/notifications' ? 'amber'   :
    href === '/forms/my'      ? 'amber'   :
    href === '/incidents'     ? 'red'     :
    href === '/failures'      ? 'red'     :
    href === '/inspections'   ? 'green'   :
    href === '/duty-log'      ? badges.dutyLogTone :
    href === '/messages'      ? 'primary' :
    href === '/pomoc'         ? 'primary' :
    href === '/board'         ? 'amber'   :
    href === '/admin/tasks'   ? 'amber'   :
    'rose';
  return { count: count > 0 ? count : 0, tone };
}

function groupBadge(items: NavItem[], badges: ReturnType<typeof useSidebarBadges>) {
  const parts = items.map((i) => badgeFor(i.href, badges)).filter((b) => b.count > 0);
  const count = parts.reduce((sum, b) => sum + b.count, 0);
  const tone = parts.reduce<BadgeTone>((t, b) => (TONE_RANK[b.tone] > TONE_RANK[t] ? b.tone : t), 'primary');
  return { count, tone };
}

function CornerBadge({ count, tone }: { count: number; tone: BadgeTone }) {
  return (
    <span
      className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full flex items-center justify-center text-[8px] font-black text-white animate-pulse"
      style={{ background: TONE_HEX[tone], boxShadow: '0 0 0 1.5px rgba(15,23,42,0.85)' }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function InlineBadge({ count, tone, className }: { count: number; tone: BadgeTone; className?: string }) {
  return (
    <span
      className={cn('ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-black text-white flex-shrink-0', className)}
      style={{ background: TONE_HEX[tone], boxShadow: `0 0 8px ${TONE_HEX[tone]}66` }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// ─── Rozwinięte grupy (pamiętane w tej przeglądarce) ───────────────────────────

const OPEN_GROUPS_KEY = 'zur-menu-otwarte-grupy';

function readOpenGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeOpenGroups(open: Set<string>) {
  try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify([...open])); } catch { /* prywatne okno — trudno */ }
}

// ─── NavItem component ────────────────────────────────────────────────────────

function NavItemComponent({
  item,
  collapsed,
  badge,
  badgeTone = 'primary',
}: {
  item: NavItem;
  collapsed: boolean;
  badge?: number;
  badgeTone?: BadgeTone;
}) {
  const location = useLocation();
  const isActive = isHrefActive(item.href, location.pathname);
  const color = item.color;

  return (
    <NavLink
      to={item.href}
      className={cn(
        'group relative flex items-center gap-3 rounded-xl px-2.5 py-1.5 text-sm font-medium transition-all duration-150',
        isActive
          ? 'text-white'
          : 'text-sidebar-foreground hover:bg-white/[0.06] hover:text-white',
        collapsed && 'justify-center px-2',
      )}
      style={isActive ? {
        background: `${color}22`,
        boxShadow: `inset 0 0 0 1px ${color}44, 0 0 16px ${color}33`,
      } : undefined}
    >
      {/* icon pill */}
      <span
        className={cn(
          'relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150',
          !isActive && 'group-hover:opacity-90',
        )}
        style={isActive
          ? { background: color, boxShadow: `0 0 14px ${color}88` }
          : { background: `${color}1f` }}
      >
        <item.icon className={cn(
          'h-4 w-4 transition-colors duration-150',
          isActive ? 'text-white' : item.iconColor,
        )} />
        {badge && badge > 0 ? <CornerBadge count={badge} tone={badgeTone} /> : null}
      </span>

      {/* label */}
      <AnimatePresence>
        {!collapsed && (
          <motion.span
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className="overflow-hidden whitespace-nowrap"
          >
            {item.label}
          </motion.span>
        )}
      </AnimatePresence>

      {/* inline badge pill (expanded) */}
      {!collapsed && badge && badge > 0 ? <InlineBadge count={badge} tone={badgeTone} /> : null}

      {/* active accent bar (right edge, expanded) */}
      {isActive && !collapsed && (
        <span
          className={cn('w-1 h-4 rounded-full flex-shrink-0', (badge && badge > 0) ? '' : 'ml-auto')}
          style={{ background: color, boxShadow: `0 0 8px ${color}99` }}
        />
      )}

      {/* tooltip when collapsed */}
      {collapsed && (
        <div className="absolute left-full ml-3 hidden group-hover:flex z-50 pointer-events-none items-center">
          <div className="rounded-lg bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground shadow-lg border whitespace-nowrap">
            {item.label}
          </div>
        </div>
      )}
    </NavLink>
  );
}

// ─── Grupa (B-679) ────────────────────────────────────────────────────────────

/** Pozycja grupy: kropka w kolorze pozycji zamiast ikony, jak w NEXOPS. */
function NavChild({ item, badges, onNavigate }: {
  item: NavItem;
  badges: ReturnType<typeof useSidebarBadges>;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  const isActive = isHrefActive(item.href, location.pathname);
  const { count, tone } = badgeFor(item.href, badges);
  const color = item.color;

  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      className={cn(
        'flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors duration-150',
        isActive ? 'font-semibold text-white' : 'text-sidebar-foreground hover:bg-white/[0.06] hover:text-white',
      )}
      style={isActive ? { background: `${color}29`, boxShadow: `inset 0 0 0 1px ${color}4d` } : undefined}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ background: color, opacity: isActive ? 1 : 0.7, boxShadow: isActive ? `0 0 8px ${color}cc` : undefined }}
      />
      <span className="truncate">{item.label}</span>
      {count > 0 ? <InlineBadge count={count} tone={tone} /> : null}
    </NavLink>
  );
}

function NavGroupComponent({
  group,
  collapsed,
  open,
  onToggle,
  badges,
}: {
  group: NavGroup;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  badges: ReturnType<typeof useSidebarBadges>;
}) {
  const location = useLocation();
  const hasActive = group.children.some((c) => isHrefActive(c.href, location.pathname));
  const { count, tone } = groupBadge(group.children, badges);
  const color = group.color;
  // Zwinięty pasek: ikona grupy otwiera wysuwaną listę obok paska (portal — pasek ma overflow-hidden).
  const btnRef = useRef<HTMLButtonElement>(null);
  const [flyout, setFlyout] = useState<{ top: number; left: number } | null>(null);
  // Wyróżnienie jak aktywnej pozycji, gdy aktywnej podpozycji nie widać (grupa zwinięta albo pasek zwinięty).
  const filled = hasActive && (collapsed || !open);

  useEffect(() => { setFlyout(null); }, [location.pathname, collapsed]);

  const onClick = () => {
    if (!collapsed) { onToggle(); return; }
    if (flyout) { setFlyout(null); return; }
    // Od prawej krawędzi paska, nie przycisku — przycisk w zwiniętym pasku nie sięga jego krawędzi.
    const r = btnRef.current?.getBoundingClientRect();
    const edge = btnRef.current?.closest('aside')?.getBoundingClientRect().right;
    if (r) setFlyout({ top: r.top, left: (edge ?? r.right) + 10 });
  };

  return (
    <div>
      <button
        ref={btnRef}
        type="button"
        onClick={onClick}
        aria-expanded={collapsed ? !!flyout : open}
        className={cn(
          'group relative flex w-full items-center gap-3 rounded-xl px-2.5 py-1.5 text-sm font-medium transition-all duration-150',
          hasActive ? 'text-white' : 'text-sidebar-foreground hover:bg-white/[0.06] hover:text-white',
          collapsed && 'justify-center px-2',
        )}
        style={filled ? {
          background: `${color}22`,
          boxShadow: `inset 0 0 0 1px ${color}44, 0 0 16px ${color}33`,
        } : undefined}
      >
        <span
          className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-all duration-150"
          style={filled
            ? { background: color, boxShadow: `0 0 14px ${color}88` }
            : { background: `${color}1f` }}
        >
          <group.icon className={cn('h-4 w-4 transition-colors duration-150', filled ? 'text-white' : group.iconColor)} />
          {collapsed && count > 0 ? <CornerBadge count={count} tone={tone} /> : null}
        </span>

        {!collapsed && (
          <>
            <span className="overflow-hidden whitespace-nowrap text-left">{group.label}</span>
            {!open && count > 0 ? <InlineBadge count={count} tone={tone} /> : null}
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 shrink-0 opacity-50 transition-transform duration-150',
                !open && '-rotate-90',
                (open || count === 0) && 'ml-auto',
              )}
            />
          </>
        )}

        {collapsed && !flyout && (
          <div className="absolute left-full ml-3 hidden group-hover:flex z-50 pointer-events-none items-center">
            <div className="rounded-lg bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground shadow-lg border whitespace-nowrap">
              {group.label}
            </div>
          </div>
        )}
      </button>

      {/* rozwinięta lista (pełny pasek) */}
      <AnimatePresence initial={false}>
        {!collapsed && open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="ml-[22px] mt-0.5 mb-1 space-y-0.5 border-l border-sidebar-border pl-3">
              {group.children.map((c) => <NavChild key={c.href} item={c} badges={badges} />)}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {collapsed && flyout && (
        <GroupFlyout
          group={group}
          pos={flyout}
          anchor={btnRef}
          badges={badges}
          onClose={() => setFlyout(null)}
        />
      )}
    </div>
  );
}

function GroupFlyout({ group, pos, anchor, badges, onClose }: {
  group: NavGroup;
  pos: { top: number; left: number };
  anchor: React.RefObject<HTMLButtonElement | null>;
  badges: ReturnType<typeof useSidebarBadges>;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(pos.top);

  // Lista przy dolnej krawędzi okna podjeżdża w górę, żeby się zmieściła.
  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 0;
    setTop(Math.max(8, Math.min(pos.top, window.innerHeight - h - 8)));
  }, [pos.top]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.current?.contains(t)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      className="fixed z-[200] min-w-[200px] rounded-xl border border-sidebar-border bg-sidebar p-1.5 shadow-2xl"
      style={{ top, left: pos.left }}
    >
      <div className="px-2.5 pb-1 pt-1 text-[9px] font-bold uppercase tracking-widest text-white/40">{group.label}</div>
      <div className="space-y-0.5">
        {group.children.map((c) => <NavChild key={c.href} item={c} badges={badges} onNavigate={onClose} />)}
      </div>
    </div>,
    document.body,
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar() {
  const { sidebarCollapsed, toggleCollapsed } = useUIStore();
  const { user } = useAuthStore();
  const { can } = usePermissions();
  const badges = useSidebarBadges();
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Set<string>>(readOpenGroups);

  // Permission-aware nav: keyless items are visible to every authenticated
  // user; keyed items require the matrix grant (ADMIN always passes).
  // Groups and sections with no visible items are hidden entirely; a group with
  // a single visible item shows that item instead (B-679).
  // B-185: „Zgłoszenia" prowadzi właściciela wprost do SKRZYNKI (/feedback).
  // Dotąd wszyscy szli na /feedback/mine, więc właściciel — jedyny, kto widzi
  // wszystkie zgłoszenia — lądował na własnych. Bramką jest `isOwner`, NIE rola:
  // kont ADMIN/DISPATCHER może być wiele (patrz FeedbackPage + feedback.controller).
  const visibleItems = (items: NavItem[]) => items
    .filter((item) => !!user && can(item.permission))
    .map((item) =>
      item.href === '/feedback/mine' && user?.isOwner ? { ...item, href: '/feedback' } : item,
    );
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.flatMap((entry): NavEntry[] => {
      if (!isGroup(entry)) return visibleItems([entry]);
      const children = visibleItems(entry.children);
      if (children.length === 0) return [];
      if (children.length === 1) return children;
      return [{ ...entry, children }];
    }),
  })).filter((section) => section.items.length > 0);

  // Grupa z otwartą stroną rozwija się sama (wejście z linku, wyszukiwarki, powiadomienia).
  useEffect(() => {
    const active = NAV_SECTIONS.flatMap((s) => s.items).find(
      (e) => isGroup(e) && e.children.some((c) => isHrefActive(c.href, location.pathname)),
    );
    if (active && !openGroups.has(active.label)) {
      const next = new Set(openGroups).add(active.label);
      setOpenGroups(next);
      writeOpenGroups(next);
    }
    // openGroups celowo poza zależnościami — zwinięcie grupy ręcznie ma zostać do następnej zmiany strony.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (label: string) => {
    const next = new Set(openGroups);
    if (next.has(label)) next.delete(label); else next.add(label);
    setOpenGroups(next);
    writeOpenGroups(next);
  };

  return (
    <motion.aside
      animate={{ width: sidebarCollapsed ? 72 : 248 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      className="relative flex h-full flex-col bg-sidebar border-r border-sidebar-border overflow-hidden"
    >
      {/* ── Brand ─────────────────────────────────────────────────────── */}
      <div
        className={cn(
          'flex h-16 items-center border-b border-sidebar-border px-4 shrink-0',
          sidebarCollapsed ? 'justify-center px-2' : 'gap-3',
        )}
      >
        {/* logo */}
        {sidebarCollapsed ? (
          <img
            src="/brand/zurops-mark.png"
            alt="ZUR OPS"
            draggable={false}
            className="h-9 w-9 object-contain select-none"
          />
        ) : (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center overflow-hidden"
            >
              <img
                src="/brand/zurops-logo-trim.png"
                alt="ZUR OPS — Centrum Operacyjne"
                draggable={false}
                className="h-9 w-auto object-contain select-none"
              />
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* ── Navigation ────────────────────────────────────────────────── */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-1">
        {visibleSections.map((section, si) => (
          <div key={section.title ?? 'start'} className={si > 0 ? 'pt-2' : ''}>
            {/* section label + divider line */}
            <AnimatePresence>
              {!sidebarCollapsed && section.title && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-2 mb-1 px-3"
                >
                  <span className="text-[9px] font-bold uppercase tracking-widest text-white/25 whitespace-nowrap">
                    {section.title}
                  </span>
                  <span className="flex-1 h-px bg-white/10" />
                </motion.div>
              )}
            </AnimatePresence>

            {/* collapsed: thin divider between sections */}
            {sidebarCollapsed && si > 0 && (
              <div className="mb-2 mx-2 border-t border-sidebar-border/50" />
            )}

            <div className="space-y-0.5">
              {section.items.map((entry) => {
                if (isGroup(entry)) {
                  return (
                    <NavGroupComponent
                      key={entry.label}
                      group={entry}
                      collapsed={sidebarCollapsed}
                      open={openGroups.has(entry.label)}
                      onToggle={() => toggleGroup(entry.label)}
                      badges={badges}
                    />
                  );
                }
                const { count, tone } = badgeFor(entry.href, badges);
                return (
                  <NavItemComponent
                    key={entry.href}
                    item={entry}
                    collapsed={sidebarCollapsed}
                    badge={count > 0 ? count : undefined}
                    badgeTone={tone}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* ── System status strip (clickable → changelog) ───────────────── */}
      <div className="border-t border-sidebar-border shrink-0">
        <AnimatePresence>
          {!sidebarCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <NavLink
                to="/pomoc#changelog"
                title={`ZUR OPS v${APP_VERSION} — wersja z ${APP_VERSION_DATE}`}
                className="flex items-center gap-2 px-6 py-4 transition-colors hover:bg-white/[0.04]"
              >
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                </span>
                <p className="text-[11px] font-medium text-white/50 tracking-wide">
                  System live · <span className="font-mono font-semibold text-white/70">v{APP_VERSION}</span>
                </p>
              </NavLink>
            </motion.div>
          )}
        </AnimatePresence>

        {/* collapsed: just the pulsing dot */}
        {sidebarCollapsed && (
          <NavLink to="/pomoc#changelog" className="flex justify-center py-3.5" title={`ZUR OPS v${APP_VERSION}`}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
          </NavLink>
        )}
      </div>

      {/* ── Collapse toggle ────────────────────────────────────────────── */}
      <button
        onClick={toggleCollapsed}
        className="absolute -right-3.5 top-20 z-10 flex h-7 w-7 items-center justify-center rounded-full border-2 border-sidebar-border bg-sidebar text-white/50 shadow-lg hover:text-primary hover:border-primary/60 transition-all duration-150"
        title={sidebarCollapsed ? 'Rozwiń menu' : 'Zwiń menu'}
      >
        {sidebarCollapsed
          ? <ChevronRight className="h-3 w-3" />
          : <ChevronLeft className="h-3 w-3" />}
      </button>
    </motion.aside>
  );
}
